import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { Input } from '../../components/Input';
import { ArrowUpDown, Search } from '../../icons/lucide';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { DARK_LAYER1 } from '../../styles/elevation';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { ViewModeToggle } from '../ViewModeToggle';
import type { DeckFilterPanelProps } from './DeckFilterPanel.types';

/**
 * The deck's filter panel — search · grid/list · sort, then a labelled row of
 * filter chips (Figma `108:29950` for Cards, `108:30081` for Quizzes).
 *
 * ONE panel serves both list surfaces because the chip set is a **prop**: the
 * Cards surface passes New / To Review / Memorised / Starred, the Quizzes
 * surface passes Quizzes / Case Studies / Oral Exams with their `countsByKind`
 * totals. Everything else — the search field, the layout toggle, the sort
 * trigger, the spacing — is identical in both frames, which is exactly why it
 * was worth lifting out of the two app-local copies.
 *
 * Platform shells are injected: mobile mounts this inside a `BottomSheet`
 * (Figma draws it as one) and hands `sortSlot`/`onSortPress` a sheet of
 * `SortMenuOptions`; web mounts it inline and wraps its own `Select`.
 *
 * The `108:*` frames moved search INTO this panel, which is what retired the
 * standalone search bar that used to sit above the Cards list.
 */
export function DeckFilterPanel<TValue extends string>({
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  onSearchFocus,
  onSearchBlur,
  view,
  onViewChange,
  gridLabel,
  listLabel,
  onSortPress,
  sortLabel,
  sortSlot,
  filtersLabel,
  chips,
  activeValues,
  onToggleChip,
  className,
  testID,
}: DeckFilterPanelProps<TValue>) {
  // The layout toggle only earns its space when the surface actually has two
  // layouts — an inert switch reads as a broken control.
  const showViewToggle = view !== undefined && onViewChange !== undefined;
  const sortControl =
    sortSlot ??
    (onSortPress ? (
      <Button
        variant="secondary"
        size="l"
        icon={ArrowUpDown}
        accessibilityLabel={sortLabel}
        testID={subTestID(testID, 'sort')}
        onPress={onSortPress}
      />
    ) : null);

  return (
    <View className={cn('w-full flex-col gap-3', className)} testID={testID}>
      {/* Row 1 — search (flex-1) · grid/list · sort. Figma `108:44853`. */}
      <View className="w-full flex-row items-center justify-center gap-3">
        <View className="min-w-0 flex-1">
          <Input
            size="l"
            leftIcon={Search}
            testID={subTestID(testID, 'search')}
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChangeText={onSearchChange}
            // Native-in-a-sheet callers chain the sheet's keyboard-target
            // handlers here; see the prop docs.
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
          />
        </View>
        {showViewToggle ? (
          <ViewModeToggle
            value={view}
            onChange={onViewChange}
            gridLabel={gridLabel}
            listLabel={listLabel}
            testID={subTestID(testID, 'view')}
          />
        ) : null}
        {sortControl}
      </View>

      {/* Row 2 — the "Filters" card (Figma `108:44814`): heading + chip row.
          This card is the INNER layer and stays. The "middle container" the
          design review removed (2026-09, DP5) was the sheet's whitish content
          surface around the search field AND this card — the product owner
          named that one — so hosts mount the panel straight on their surface
          (mobile: no `<BottomSheetContent>`). */}
      <View
        className={cn(
          'w-full flex-col gap-2 rounded-sheet-media border border-secondary-200 bg-secondary-50/70 p-3',
          // Dark: nested in its host surface — never darker (elevation.ts).
          DARK_LAYER1,
        )}>
        <View className="w-full flex-row items-center px-2">
          {/* Figma `108:44876` is B2 (Medium 14/20, secondary-600). */}
          <Text className="flex-1 text-b2 font-medium text-secondary-600 native:font-mona-medium">
            {filtersLabel}
          </Text>
        </View>
        <View className="w-full flex-row flex-wrap items-start gap-2">
          {chips.map((chip) => {
            const active = activeValues.has(chip.value);
            return (
              <Chip
                key={chip.value}
                testID={subTestID(testID, `option-${chip.value}`)}
                size="l"
                tint={active ? chip.tint : 'neutral'}
                contentTint={chip.tint}
                leftIcon={chip.icon}
                // A kind with no history can only filter the board down to
                // empty. An ACTIVE chip stays pressable regardless, so a
                // deep link into an empty kind (or deleting a kind's last
                // item) can never leave an undeselectable filter behind.
                disabled={!active && chip.count === 0}
                onPress={() => onToggleChip(chip.value)}>
                {`${chip.label}: ${chip.count}`}
              </Chip>
            );
          })}
        </View>
      </View>
    </View>
  );
}
(DeckFilterPanel as { displayName?: string }).displayName = 'DeckFilterPanel';
